import type { ElementPosition } from '@/types/alignment';
import type { LightingFixture } from '@/types/database';
import type { LightingControl, LVProduct, Room } from '@/types/database.types';
import type { FloorPlanAnnotation } from '@/types/floorPlanViewer';

export interface ViewportBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Collect positions of all elements on the floor plan for snap calculations
 * Optionally filters to only include elements within or near the visible viewport
 */
export function collectElementPositions(
  fixtures: LightingFixture[],
  controls: LightingControl[],
  products: LVProduct[],
  annotations: FloorPlanAnnotation[],
  rooms: Room[],
  excludeIds: string[] = [],
  viewportBounds?: ViewportBounds
): ElementPosition[] {
  // Helper to check if element is within or near viewport for snap calculations
  const isNearViewport = (x: number, y: number, width: number, height: number): boolean => {
    if (!viewportBounds) return true; // No filtering if no bounds provided

    // Add a generous margin (in image coordinates) to include elements just outside viewport
    // This ensures we can snap to nearby off-screen elements
    const margin = 500; // pixels in image space - increased for better snap range

    const elementRight = x + width;
    const elementBottom = y + height;

    return !(
      elementRight < viewportBounds.minX - margin ||
      x > viewportBounds.maxX + margin ||
      elementBottom < viewportBounds.minY - margin ||
      y > viewportBounds.maxY + margin
    );
  };

  const positions: ElementPosition[] = [];

  // Collect fixture positions
  for (const fixture of fixtures) {
    if (excludeIds.includes(fixture.id)) continue;

    const width = fixture.width || 40;
    const height = fixture.height || 40;
    const x = fixture.x_position;
    const y = fixture.y_position;

    if (!isNearViewport(x, y, width, height)) continue;

    positions.push({
      id: fixture.id,
      type: 'fixture',
      x,
      y,
      width,
      height,
      rotation: fixture.rotation,
    });
  }

  // Collect control positions
  for (const control of controls) {
    if (excludeIds.includes(control.id)) continue;

    const width = control.width || 40;
    const height = control.height || 40;
    const x = control.x_position ?? 0;
    const y = control.y_position ?? 0;

    if (!isNearViewport(x, y, width, height)) continue;

    positions.push({
      id: control.id,
      type: 'control',
      x,
      y,
      width,
      height,
      rotation: control.rotation ?? undefined,
    });
  }

  // Collect LV product positions
  for (const product of products) {
    if (excludeIds.includes(product.id)) continue;

    const width = product.width || 40;
    const height = product.height || 40;
    const x = product.x_position;
    const y = product.y_position;

    if (!isNearViewport(x, y, width, height)) continue;

    positions.push({
      id: product.id,
      type: 'product',
      x,
      y,
      width,
      height,
      rotation: product.rotation ?? undefined,
    });
  }

  // Collect annotation positions
  for (const annotation of annotations) {
    if (excludeIds.includes(annotation.id)) continue;

    let width = 100;
    let height = 100;

    // Calculate annotation bounds based on type
    if (annotation.type === 'rectangle') {
      width = Math.abs(annotation.endX - annotation.x);
      height = Math.abs(annotation.endY - annotation.y);
    } else if (annotation.type === 'circle') {
      const radius = Math.sqrt(
        Math.pow(annotation.endX - annotation.x, 2) +
        Math.pow(annotation.endY - annotation.y, 2)
      );
      width = radius * 2;
      height = radius * 2;
    } else if (annotation.type === 'text') {
      // Approximate text bounds (will need refinement based on actual text rendering)
      width = (annotation.text?.length || 10) * 8;
      height = 24;
    } else if (annotation.type === 'image') {
      width = annotation.width || 100;
      height = annotation.height || 100;
    }

    if (!isNearViewport(annotation.x, annotation.y, width, height)) continue;

    positions.push({
      id: annotation.id,
      type: 'annotation',
      x: annotation.x,
      y: annotation.y,
      width,
      height,
      rotation: annotation.rotation,
    });
  }

  // Collect room positions
  for (const room of rooms) {
    if (excludeIds.includes(room.id)) continue;

    // Calculate room bounds from polygon points
    if (room.polygon && room.polygon.length > 0) {
      const xs = room.polygon.map(p => p[0]);
      const ys = room.polygon.map(p => p[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const width = maxX - minX;
      const height = maxY - minY;

      if (!isNearViewport(minX, minY, width, height)) continue;

      positions.push({
        id: room.id,
        type: 'room',
        x: minX,
        y: minY,
        width,
        height,
      });
    }
  }

  return positions;
}
